import ExcelJS from "exceljs";
import {HR_ID_COLUMN, NAME_COLUMN} from "../constants";

class Excel {
  constructor() {
    if (!Excel.instance) {
      Excel.instance = this;
    }

    return Excel.instance;
  }

  _createWorkbook() {
    return new ExcelJS.Workbook();
  }

  _parseCsvLine(line){
    const values = [];
    let currentValue = '';
    let insideQuotes = false;

    for (const char of line) {
      if (char === '"') {
        insideQuotes = !insideQuotes;
        continue;
      }

      if (char === ',' && !insideQuotes) {
        values.push(currentValue.trim());
        currentValue = '';
        continue;
      }

      currentValue += char;
    }

    values.push(currentValue.trim());

    return values;
  };

  parseXlsxFile(file) {
    return new Promise((resolve, reject) => {
      const fileReader = new FileReader();
      fileReader.onload = async (event) => {
        try {
          const workbook = new ExcelJS.Workbook();
          await workbook.xlsx.load(event.target.result);
          const worksheet = workbook.worksheets[0];
          const records = [];

          const headerRow = worksheet.getRow(1);
          const headers = [];
          headerRow.eachCell((cell, columnIndex) => {
            const headerText = cell.value ? cell.value.toString().trim(): '';
            headers.push(headerText);
          });

          worksheet.eachRow((row, rowNumber) => {
            if (rowNumber === 1) return;

            const record = {};
            let hasData = false;

            headers.forEach((header, columnIndex) => {
              const cellValue = row.getCell(columnIndex + 1).value?.toString()?.trim() || '';
              record[header] = cellValue;
              if (cellValue) hasData = true;
            });

            if (hasData) records.push(record);
          });

          resolve(records);
        } catch (error) {
          reject(error);
        }
      };
      fileReader.onerror = () => reject(new Error('Failed to read file.'));
      fileReader.readAsArrayBuffer(file);
    });
  };


  parseCsvFile(file){
    return new Promise((resolve, reject) => {
      const fileReader = new FileReader();
      fileReader.onload = (event) => {
        try {
          const text = event.target.result;
          const lines = text.split('\n').filter(line => line.trim());

          if (lines.length < 2) {
            resolve([]);
            return;
          }

          const headers = this._parseCsvLine(lines[0]).map((header, index) => header.trim() || '');
          const records = [];

          for (let rowIndex = 1; rowIndex < lines.length; rowIndex++) {
            const rowValues = this._parseCsvLine(lines[rowIndex]);
            if (rowValues.length < headers.length) continue;

            const record = {};
            let hasData = false;

            headers.forEach((header, columnIndex) => {
              const value = rowValues[columnIndex] || '';
              record[header] = value;
              if (value) hasData = true;
            });

            if (hasData) records.push(record);
          }

          resolve(records);
        } catch (error) {
          reject(error);
        }
      };
      fileReader.onerror = () => reject(new Error('Failed to read file.'));
      fileReader.readAsText(file);
    });
  };

  async generateDownloadLetterExcel(excelHeaders, data) {
    const headers = [NAME_COLUMN, HR_ID_COLUMN, ...excelHeaders];
    const workbook =  this._createWorkbook();
    const worksheet = workbook.addWorksheet('Sheet1');

    worksheet.addRow(headers);

    data.forEach(item => {
      const rowValues = headers.map(header => item[header]);

      worksheet.addRow(rowValues);
    });

    worksheet.columns = headers.map((header) => {
      const getColumnWidth = Math.max(...data?.map(item => String(item[header] || '')?.length), header?.length) + 2;

      return {
        header: header,
        key: header,
        width: getColumnWidth < 15 ? 15 : getColumnWidth
      }
    });

    const buffer = await workbook.xlsx.writeBuffer();
    const blob = new Blob([buffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');

    link.href = url;
    link.download = 'export_user_data.xlsx';
    link.click();
    window.URL.revokeObjectURL(url);
  }
}

const ExcelService = new Excel();
Object.freeze(ExcelService);

export {ExcelService};
